import LogoGradientIcon from '../../icons/LogoGradientIcon'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectUser, setSession } from '../../reducers/users'
import { SessionState } from '../../types/UserTypes'
import { inPov } from '../../utils/session'
import { Button } from '@radix-ui/themes'
import { selectSettings, setDevicePolicy } from '../../reducers/settings'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

interface IPoliciesHeaderProps {
  pageTitle?: string
  overviewScreen?: boolean
  createScreen?: boolean
}

const PoliciesHeader = (props: IPoliciesHeaderProps) => {
  const { pageTitle, overviewScreen, createScreen } = props
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { session, currentPage } = useSelector(selectUser)
  const { devicePolicy } = useSelector(selectSettings)
  const [policyName, setPolicyName] = useState('')

  useEffect(() => {
    const goToConfigured = localStorage.getItem('SSO_CONFIGURED_COOKIE')

    if (goToConfigured === 'true') {
      navigate('/settings')
    }
    if (window.location.search.includes('error=sso-unauthorized')) {
      navigate('/sso-error')
    }

    if (import.meta.env.VITE_PRETTY_ENV !== 'Production' && import.meta.env.VITE_ON_PREM === 'false') {
      document.title = '[' + import.meta.env.VITE_PRETTY_ENV + '] ' + 'iVerify EDR Portal'
    }
  }, [])

  useEffect(() => {
    const storedSession = localStorage.getItem('session') as string

    if (currentPage === 'SSO_ERROR') {
      localStorage.setItem('session', '{}')
      localStorage.removeItem('SSO_CONFIGURED_COOKIE')
      dispatch(setSession(undefined))
    } else if (!session && storedSession === '{}' && currentPage !== 'SSO_ERROR') {
      localStorage.setItem('session', '{}')
      localStorage.removeItem('SSO_CONFIGURED_COOKIE')
      dispatch(setSession(undefined))
      navigate('/login')
    } else if (!session && storedSession !== '{}') {
      dispatch(setSession(JSON.parse(storedSession) as SessionState))
    }
  }, [session])

  useEffect(() => {
    if (devicePolicy) {
      setPolicyName(devicePolicy.name)
    } else {
      setPolicyName(pageTitle || 'Policies')
    }
  }, [pageTitle, devicePolicy])

  const handleSaveClick = () => {
    window.dispatchEvent(new Event('saveDevicePolicy'))
  }

  return (
    <>
      <div className="w-full absolute h-[74px] flex" style={{ top: inPov(session) ? '42px' : '0px' }}>
        {/* iVerify Logo */}
        <div className="h-[26px] flex items-center mt-6 ml-6 w-[182px]">
          <LogoGradientIcon height="26" width="71" />
        </div>
        <div className="flex items-center flex-grow">
          {/* Page Title */}
          {!overviewScreen && !createScreen && <div className="font-semibold text-mainText text-xl">{pageTitle}</div>}
          {overviewScreen && !createScreen && (
            <>
              <div
                className="font-semibold text-neutral-11 dark:text-neutral-9 text-xl cursor-pointer shrink-0"
                onClick={() => {
                  dispatch(setDevicePolicy(undefined))
                  navigate('/device-policies')
                }}
              >
                {pageTitle}
              </div>
              <IconChevronRight size={18} className="text-neutral-11 dark:text-neutral-9 mx-1.5 shrink-0" />
              <div className="font-semibold text-xl text-mainText truncate max-w-[400px]">{policyName}</div>
            </>
          )}
          {createScreen && (
            <>
              <IconChevronLeft
                size={18}
                className="text-neutral-11 dark:text-neutral-9 ml-1.5 mr-3 shrink-0 cursor-pointer"
                onClick={() => navigate('/device-policies')}
              />
              <div className="font-semibold text-xl text-mainText truncate max-w-[400px]">{pageTitle}</div>
            </>
          )}
        </div>
        {!overviewScreen && !createScreen && (
          <div className="flex items-center">
            <Button
              className="h-7 mr-6 bg-mainPurple text-xs hover:bg-tertiaryPurple active:active:bg-tertiaryPurple cursor-pointer"
              onClick={() => navigate('/create-device-policy')}
            >
              Create Policy
            </Button>
          </div>
        )}
        {overviewScreen && (
          <>
            <div className="flex items-center">
              <Button
                className="h-[30px] mr-6 bg-mainPurple text-xs hover:bg-tertiaryPurple active:active:bg-tertiaryPurple cursor-pointer"
                onClick={() => handleSaveClick()}
              >
                Save Changes
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default PoliciesHeader
